import { baseApi } from './base.api';
import { API_VERSION, PATH_USERS_API } from '../constants/api-routes';
import { FetchArgs } from '@reduxjs/toolkit/query';
import type { ProductKey } from 'src/constants/products';

interface Account {
  id: number;
  company_name: string;
}

export interface User {
  id: number;
  role: string;
  is_active: boolean;
  email: string;
  first_name: string;
  last_name: string;
  account: number;
  managed_accounts?: {
    id: number;
    account: Account;
  }[];
  // Listed for the account's users: the products and projects each one can access.
  products?: string[];
  projects?: UserProject[];
}

export interface UserProject {
  id: number;
  name: string;
  project_type: ProductKey;
  // The user's team membership in the project, when they have one.
  team_member_id?: number;
}

export const usersService = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getUsersMe: build.query<User, void>({
      query: () => ({
        url: `${API_VERSION.V1}/${PATH_USERS_API.ME}`,
      }),
      providesTags: ['UserMe'],
    }),
    updateUsersMe: build.mutation({
      query: (body) => ({
        url: `${API_VERSION.V1}/${PATH_USERS_API.ME}`,
        method: 'PUT',
        body: body,
      }),
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedUser } = await queryFulfilled;
          dispatch(
            usersService.util.updateQueryData('getUsersMe', undefined, (draft) => {
              Object.assign(draft, updatedUser);
            }),
          );
        } catch {}
      },
    }),
    getUsersAll: build.query<User[], void>({
      query: (): FetchArgs => ({
        url: `${API_VERSION.V1}/${PATH_USERS_API.ALL}`,
      }),
      providesTags: ['UsersAll'],
    }),
    createUser: build.mutation<User, Record<string, unknown>>({
      query: (body) => ({
        url: `${API_VERSION.V1}/${PATH_USERS_API.CREATE}`,
        method: 'POST',
        body: body,
      }),
      async onQueryStarted({}, { dispatch, queryFulfilled }) {
        try {
          const { data: newUser } = await queryFulfilled;

          dispatch(
            usersService.util.updateQueryData('getUsersAll', undefined, (draft) => {
              draft.push(newUser);
            }),
          );
        } catch {}
      },
      invalidatesTags: ['CreateUser'],
    }),
    updateUser: build.mutation<User, { userId: number; body: Record<string, unknown> }>({
      query: ({ userId, body }) => ({
        url: `${API_VERSION.V1}/${PATH_USERS_API.ROOT}/${userId}${PATH_USERS_API.UPDATE}`,
        method: 'PUT',
        body: body,
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedUser } = await queryFulfilled;
          dispatch(
            usersService.util.updateQueryData('getUsersAll', undefined, (draft) => {
              const currentUser = draft.find((user) => user.id === updatedUser.id);
              Object.assign(currentUser, updatedUser);
            }),
          );
        } catch (err) {
          console.error('Error updating user:', err);
        }
      },
      invalidatesTags: ['ProjectById'],
    }),
    deleteUser: build.mutation<unknown, { userId: number }>({
      query: ({ userId }) => ({
        url: `${API_VERSION.V1}/${PATH_USERS_API.ROOT}/${userId}${PATH_USERS_API.DELETE}`,
        method: 'DELETE',
      }),
      // getUsersAll provides an id-less tag, so the whole list is refreshed.
      invalidatesTags: (result, error) => (error ? [] : ['UsersAll']),
    }),
  }),
});

export const {
  useGetUsersMeQuery,
  useUpdateUsersMeMutation,
  useGetUsersAllQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
} = usersService;
